/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Grid, Paper, TextField, Typography } from "@material-ui/core";
import PageLoading from "components/PageLoading";
import { OptionGroup, StyledPaper } from "components/Styled";
import PillButton from "components/Forms/PillButton";
import { Button } from "components/Forms/StyledComponents";
import apiRequest, { RequestMethod } from "utils/apiRequestWithErrorCode";
import Predicate from "./FilterPolicyPredicate";
import { hasErrors, validate } from "./validations";

import {
  FilterPolicy,
  FilterPolicyReqBody,
  FilterPolicyPredicate,
} from "@dashboard-v3/api";
import { FormState, FormTouched } from "./types";

type EditState = {
  pathname: string;
  state: { policyId: string; selectedPolicy: FormState };
};

type FormData = {
  values: FormState;
  touched: FormTouched;
};

type FormDataUpdate = Partial<{
  values: Partial<FormState>;
  touched: Partial<FormTouched>;
}>;

export default function FilterPolicyForm() {
  const { t } = useTranslation("filterPolicies");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { id: paramId } = useParams();
  const { state, pathname }: EditState = useLocation();
  const isEdit = pathname.includes("/edit") && Boolean(paramId);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FormData>(initData(state));

  const { values, touched } = data;
  const errors = validate(values, t);

  function update(update: FormDataUpdate) {
    setData(prev => ({
      values: update.values
        ? { ...prev.values, ...update.values }
        : prev.values,
      touched: update.touched
        ? { ...prev.touched, ...update.touched }
        : prev.touched,
    }));
  }

  useEffect(() => {
    const fetchPolicyById = async (id: string) => {
      setLoading(true);
      try {
        const path = `/filter-policies/${id}`;
        const policy = await apiRequest<FilterPolicy>("GET", path);
        update({ values: policy });
      } catch (e: unknown) {
        enqueueSnackbar(t("form.notification.fetchByIdError"), {
          variant: "error",
        });
        history.push("/filter-policies");
      } finally {
        setLoading(false);
      }
    };

    async function handleEditData() {
      if (state?.policyId === paramId) return;
      await fetchPolicyById(paramId);
    }

    if (isEdit) {
      handleEditData();
    }
  }, []);

  function addPredicate() {
    const [newPredicate] = initForm().predicates;
    update({
      values: { predicates: [...values.predicates, newPredicate] },
      touched: { predicates: [...(touched.predicates || []), {}] },
    });
  }

  function removePredicate(index: number) {
    if (values.predicates.length <= 1) return;
    update({
      values: {
        predicates: values.predicates.filter((_, i) => i !== index),
      },
      touched: {
        predicates: (touched.predicates || []).filter((_, i) => i !== index),
      },
    });
  }

  function onPredicateChange(
    idx: number,
    patch: Partial<FilterPolicyPredicate>
  ) {
    const newPredicates = values.predicates.map((p, i) =>
      i === idx ? { ...p, ...patch } : p
    );
    update({ values: { predicates: newPredicates } });
  }

  const onInputChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    update({ values: { [name]: value } });
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    const fieldName = e.target.name;
    if (!fieldName) return;
    update({ touched: { [fieldName]: true } });
  };

  function handlePredicateBlur(idx: number) {
    return (field: "variableIdList") => {
      const next = [...(touched.predicates || [])];
      next[idx] = { ...next[idx], [field]: true };
      update({ touched: { predicates: next } });
    };
  }

  function markAllTouched() {
    update({
      touched: {
        name: true,
        description: true,
        predicates: values.predicates.map(() => ({ variableIdList: true })),
      },
    });
  }

  const savePolicy = async (body: FilterPolicyReqBody, id?: string) => {
    setLoading(true);

    try {
      const { path, method } = {
        path: id ? `/filter-policies/${id}` : "/filter-policies",
        method: id ? "PUT" : ("POST" as RequestMethod),
      };
      await apiRequest<FilterPolicy>(method, path, body);
      enqueueSnackbar(t("form.notification.savedPolicy"), {
        variant: "success",
      });
      history.push("/filter-policies");
    } catch (error) {
      enqueueSnackbar(t("form.notification.saveError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (hasErrors(errors)) {
      markAllTouched();
      return;
    }

    if (state && isEdit) {
      savePolicy(values, state.policyId);
    } else {
      savePolicy(values);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PageLoading loading={loading} />
      <Grid container spacing={5}>
        <Grid item lg={8} xs={12}>
          <Paper style={{ padding: "15px 24px 24px 24px" }}>
            <Typography
              gutterBottom
              variant="h6"
              style={{ marginBottom: "20px" }}
            >
              {t(`form.title.${isEdit ? "edit" : "new"}`)}
            </Typography>
            <OptionGroup>
              <TextField
                name="name"
                label={t("form.name.label")}
                variant="outlined"
                value={values.name}
                onChange={onInputChange}
                onBlur={handleBlur}
                fullWidth
                error={Boolean(touched.name && errors.name)}
                helperText={(touched.name && errors.name) || ""}
                inputProps={{ "data-testid": "fp-name-input" }}
              />
            </OptionGroup>
            <OptionGroup>
              <TextField
                name="description"
                label={t("form.description.label")}
                variant="outlined"
                value={values.description || ""}
                onChange={onInputChange}
                onBlur={handleBlur}
                fullWidth
                error={Boolean(touched.description && errors.description)}
                helperText={(touched.description && errors.description) || ""}
                inputProps={{ "data-testid": "fp-description-input" }}
              />
            </OptionGroup>
          </Paper>
          {values.predicates.map((_, i) => (
            <Predicate
              key={i}
              index={i}
              predicate={values.predicates[i]}
              canRemove={values.predicates.length > 1}
              onRemovePredicate={() => removePredicate(i)}
              onPredicateChange={patch => onPredicateChange(i, patch)}
              errors={errors.predicates?.[i]}
              touched={touched.predicates?.[i]}
              onBlur={handlePredicateBlur(i)}
            />
          ))}
          <PillButton
            wording={t("form.addConditionBtn")}
            onClick={addPredicate}
            lineSeparator="full"
            data-testid="fp-add-condition-btn"
          />

          <StyledPaper style={{ padding: "25px 24px" }}>
            <Button
              type="submit"
              size="large"
              fullWidth
              wording={t("form.savePolicyBtn")}
              data-testid="fp-save-btn"
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </form>
  );
}

function initForm(savedPolicy?: FormState): FormState {
  if (savedPolicy) return savedPolicy;

  return {
    name: "",
    description: "",
    predicates: [
      {
        match: true,
        firstMatch: false,
        variableIdList: [],
      },
    ],
  };
}

function initData(state: EditState["state"]) {
  return {
    values: initForm(state?.selectedPolicy),
    touched: {},
  };
}
